import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Canvas, loadSVGFromString, Image as FabricImage, Path, Control, util, Text, IText, Gradient, Rect, Circle, Triangle } from 'fabric';
import { preloadCarAssets, getCachedImage } from '../../utils/imagePreloader';
import { logger } from '../../utils/logger';
import { 
  Trash2, 
  Lock, 
  Unlock, 
  Copy, 
  MoreVertical, 
  ArrowUp, 
  ArrowDown, 
  FlipHorizontal,
  ArrowLeft,
  HelpCircle,
  Upload,
  Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '../ui/dialog';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '../ui/tooltip';
import { toast } from 'react-hot-toast';

// Car color gradients matching the sidebar color picker
const CAR_COLOR_GRADIENTS = {
  '#000000': { colorStops: [{ offset: 0, color: '#000000' }, { offset: 0.5, color: '#1a1a1a' }, { offset: 1, color: '#000000' }] },
  '#FFFFFF': { colorStops: [{ offset: 0, color: '#FFFFFF' }, { offset: 0.5, color: '#f5f5f5' }, { offset: 1, color: '#FFFFFF' }] },
  '#C0C0C0': { colorStops: [{ offset: 0, color: '#d4d4d4' }, { offset: 0.5, color: '#a8a8a8' }, { offset: 1, color: '#c8c8c8' }] },
  '#3C4043': { colorStops: [{ offset: 0, color: '#4a4d50' }, { offset: 0.5, color: '#2d2f32' }, { offset: 1, color: '#3c4043' }] },
  '#8B8D8F': { colorStops: [{ offset: 0, color: '#9a9c9e' }, { offset: 0.5, color: '#7a7c7e' }, { offset: 1, color: '#8b8d8f' }] },
  '#1A3B5D': { colorStops: [{ offset: 0, color: '#244a6f' }, { offset: 0.5, color: '#152e47' }, { offset: 1, color: '#1a3b5d' }] },
  '#C1282D': { colorStops: [{ offset: 0, color: '#d63841' }, { offset: 0.5, color: '#a71f24' }, { offset: 1, color: '#c1282d' }] },
  '#2C3F2E': { colorStops: [{ offset: 0, color: '#384d3a' }, { offset: 0.5, color: '#1f2b20' }, { offset: 1, color: '#2c3f2e' }] },
  '#8A7968': { colorStops: [{ offset: 0, color: '#9a8978' }, { offset: 0.5, color: '#7a6958' }, { offset: 1, color: '#8a7968' }] }
};

const getCarColorFill = (color, canvasWidth, canvasHeight) => {
  const gradientDef = CAR_COLOR_GRADIENTS[color.toUpperCase()];
  
  if (gradientDef) {
    // Create a diagonal gradient (top-left to bottom-right) matching CSS 135deg
    return new Gradient({
      type: 'linear',
      coords: {
        x1: 0,
        y1: 0,
        x2: canvasWidth,
        y2: canvasHeight
      },
      colorStops: gradientDef.colorStops
    });
  }
  
  // Fallback to solid color for custom colors
  return color;
};

const truncateFilename = (filename, maxLength = 20) => {
  if (!filename || filename.length <= maxLength) return filename;
  
  const extension = filename.split('.').pop();
  const nameWithoutExt = filename.slice(0, -(extension.length + 1));
  
  const halfLength = Math.floor((maxLength - 3) / 2);
  const start = nameWithoutExt.slice(0, halfLength);
  const end = nameWithoutExt.slice(-halfLength);
  
  return `${start}...${end}.${extension}`;
};

const CarPreview = forwardRef(({ 
  selectedColor, 
  selectedView,
  setSelectedView,
  placedGraphics,
  setPlacedGraphics,
  readOnly = false,
  updateGraphicPosition,
  updateGraphicSize,
  updateGraphicRotation,
  deleteGraphic,
  updateGraphicZIndex,
  carDimensions,
  placeText,
  onTextSelect,
  selectedCarModel,
  carModelData,
  duplicateGraphic,
  onBackToCarSelection,
  undo,
  redo,
  canUndo,
  canRedo,
  handleFileCheck,
  activeShapeTool = null,
  shapeProperties = { fill: '#000000', stroke: 'transparent', strokeWidth: 0, opacity: 1 },
  placeShape,
  onShapeSelect
}, ref) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const carPathRef = useRef(null);
  const overlayRef = useRef(null);
  const graphicsStateRef = useRef({
    'Sida Vänster': {},
    'Sida Höger': {},
    'Fram': {},
    'Bak': {},
    'Topp': {}
  });
  const [isControlActive, setIsControlActive] = useState(false);
  const [isViewChanging, setIsViewChanging] = useState(false);
  const tempGraphicsRef = useRef([]);
  const [activeObject, setActiveObject] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [lockedObjects, setLockedObjects] = useState(new Set());
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [showRotationTooltip, setShowRotationTooltip] = useState(false);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [rotationTooltipPosition, setRotationTooltipPosition] = useState({ x: 0, y: 0 });
  const [isRotationSnapping, setIsRotationSnapping] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  
  // Shape drawing state
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const [drawingStartPoint, setDrawingStartPoint] = useState(null);
  const [tempShape, setTempShape] = useState(null);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const showKeyboardShortcutsRef = useRef(false);
  const keyboardMoveTimeoutRef = useRef(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
  
  // Keep ref in sync with state
  useEffect(() => {
    showKeyboardShortcutsRef.current = showKeyboardShortcuts;
  }, [showKeyboardShortcuts]);

  // Store placedGraphics in a ref to access latest value in event handlers
  const placedGraphicsRef = useRef(placedGraphics);
  
  // Keep the ref updated with latest placedGraphics
  useEffect(() => {
    placedGraphicsRef.current = placedGraphics;
  }, [placedGraphics]);

  // Set CSS variables for shadow overlay dimensions based on selected view and car model
  useEffect(() => {
    if (containerRef.current && selectedView && carModelData?.shadowOverlay) {
      const shadowConfig = carModelData.shadowOverlay[selectedView];
      if (shadowConfig) {
        containerRef.current.style.setProperty('--shadow-width', shadowConfig.width);
        containerRef.current.style.setProperty('--shadow-height', shadowConfig.height);
        containerRef.current.style.setProperty('--shadow-bottom', shadowConfig.bottom);
      }
    }
  }, [selectedView, carModelData]);

  // Load SVG and render to canvas overlay (for asset protection)
  useEffect(() => {
    if (!overlayRef.current || !selectedView || !carModelData?.viewImages) {
      return;
    }

    const canvas = overlayRef.current;
    const ctx = canvas.getContext('2d');
    const svgUrl = carModelData.viewImages[selectedView];

    // Get device pixel ratio for high-DPI displays (Retina, 4K, etc.)
    const dpr = window.devicePixelRatio || 1;

    // Load the SVG
    fetch(svgUrl)
      .then(response => response.text())
      .then(svgText => {
        // Create a blob from SVG text
        const blob = new Blob([svgText], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        
        // Create an image and draw it to canvas
        const img = new Image();
        img.onload = () => {
          // Set canvas dimensions (CSS display size)
          const containerWidth = canvasDimensions.width || img.width;
          const containerHeight = canvasDimensions.height || img.height;
          
          // Set actual canvas resolution (higher for sharp rendering)
          canvas.width = containerWidth * dpr;
          canvas.height = containerHeight * dpr;
          
          // Scale canvas back down for CSS (keeps visual size same)
          canvas.style.width = `${containerWidth}px`;
          canvas.style.height = `${containerHeight}px`;
          
          // Scale context to match device pixel ratio
          ctx.scale(dpr, dpr);
          
          // Enable high-quality image rendering
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Calculate aspect ratios
          const imgAspect = img.width / img.height;
          const canvasAspect = containerWidth / containerHeight;
          
          let drawWidth, drawHeight, offsetX, offsetY;
          
          // Preserve aspect ratio (object-fit: contain behavior)
          if (imgAspect > canvasAspect) {
            // Image is wider - fit to width
            drawWidth = containerWidth;
            drawHeight = containerWidth / imgAspect;
            offsetX = 0;
            offsetY = (containerHeight - drawHeight) / 2;
          } else {
            // Image is taller - fit to height
            drawHeight = containerHeight;
            drawWidth = containerHeight * imgAspect;
            offsetX = (containerWidth - drawWidth) / 2;
            offsetY = 0;
          }
          
          // Clear canvas and draw image with preserved aspect ratio
          ctx.clearRect(0, 0, containerWidth, containerHeight);
          ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
          
          // Clean up
          URL.revokeObjectURL(url);
        };
        img.src = url;
      })
      .catch(err => {
        logger.error('Error loading SVG overlay:', err);
      });
  }, [selectedView, carModelData, canvasDimensions]);

  const menuOptions = [
    {
      label: 'Flytta upp ett lager',
      icon: <ArrowUp size={14} />,
      onClick: () => handleZIndexChange(activeObject, 1)
    },
    {
      label: 'Flytta ner ett lager',
      icon: <ArrowDown size={14} />,
      onClick: () => handleZIndexChange(activeObject, -1)
    },
    {
      label: 'Duplicera',
      icon: <Copy size={14} />,
      onClick: () => handleDuplicate(activeObject)
    },
    {
      label: activeObject?.flipX ? 'Vänd åt höger' : 'Vänd åt vänster',
      icon: <FlipHorizontal size={14} />,
      onClick: () => handleFlip(activeObject)
    },
    {
      label: 'Ta bort',
      icon: <Trash2 size={14} />,
      onClick: () => handleDelete(activeObject),
      className: 'text-red-500 hover:text-red-600'
    }
  ];

  // Function to clear the canvas
  const clearCanvas = () => {
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.clear();
      fabricCanvasRef.current.backgroundColor = 'transparent';
    }
  };

  // Event handler functions
  const handleObjectMoving = () => setIsControlActive(true);
  const handleObjectScaling = () => setIsControlActive(true);
  const handleObjectRotating = (e) => {
    const obj = e.target;
    const isSnapping = e.e?.shiftKey;
    
    // Enable/disable snap angle based on Shift key
    if (isSnapping) {
      obj.snapAngle = 45;
    } else {
      obj.snapAngle = 0;
    }
    
    const displayAngle = Math.round(obj.angle);
    setRotationAngle(displayAngle);
    setIsRotationSnapping(isSnapping);
    setShowRotationTooltip(true);
    
    // Calculate position near rotation control
    const zoom = fabricCanvasRef.current.getZoom();
    const containerRect = containerRef.current.getBoundingClientRect();

    // Get the center point of the object
    const center = obj.getCenterPoint();
    
    // Calculate the position of the rotation control (30 pixels above the center)
    const rotationControlY = center.y - 30;
    
    // Convert to screen coordinates
    const x = (center.x * zoom) + containerRect.left;
    const y = (rotationControlY * zoom) + containerRect.top - 20; // Position above rotation control
    
    setRotationTooltipPosition({ x, y });
  };

  const handleSelectionCreated = (e) => {
    const selectedObject = e.selected[0];
    
    // Prevent selecting the car path
    if (selectedObject?.data?.type === 'car') {
      fabricCanvasRef.current.discardActiveObject();
      fabricCanvasRef.current.renderAll();
      return;
    }
    
    setIsControlActive(true);
    setActiveObject(selectedObject);
    updateTooltipPosition(selectedObject);

    // Enter text edit mode when selecting a text object
    if (selectedObject?.type === 'i-text') {
      const view = selectedObject.data?.view || selectedView;
      const textObject = placedGraphicsRef.current[view]?.find(item => {
        return item.id === selectedObject.data?.id;
      });
      
      if (textObject && onTextSelect) {
        onTextSelect(textObject);
      }
    }
    
    // Notify parent when a shape is selected
    if (selectedObject?.data?.type === 'shape') {
      const view = selectedObject.data?.view || selectedView;
      const shapeObject = placedGraphicsRef.current[view]?.find(item => {
        return item.id === selectedObject.data?.id;
      });
      
      if (shapeObject && onShapeSelect) {
        onShapeSelect(shapeObject);
      }
    }
  };

  const handleSelectionCleared = () => {
    setIsControlActive(false);
    setActiveObject(null);
    if (onTextSelect) {
      onTextSelect(null);
    }
    if (onShapeSelect) {
      onShapeSelect(null);
    }
  };

  const handleMouseDown = () => {
    // Mouse down handler if needed
  };

  const handleMouseUp = () => {
    setShowRotationTooltip(false);
  };

  const handleKeyDown = useCallback((e) => {
    console.log('Key pressed:', e.key, 'Ctrl:', e.ctrlKey, 'Meta:', e.metaKey, 'Shift:', e.shiftKey);
    const activeObject = fabricCanvasRef.current?.getActiveObject();
    
    // Ctrl+Z - Undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      console.log('Ctrl+Z pressed! undo:', undo, 'canUndo:', canUndo);
      e.preventDefault();
      if (undo && canUndo) {
        console.log('Calling undo...');
        undo();
      } else {
        console.log('Cannot undo - undo:', undo, 'canUndo:', canUndo);
      }
      return;
    }
    
    // Ctrl+Y or Ctrl+Shift+Z - Redo
    if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      console.log('Redo shortcut pressed! redo:', redo, 'canRedo:', canRedo);
      e.preventDefault();
      if (redo && canRedo) {
        console.log('Calling redo...');
        redo();
      } else {
        console.log('Cannot redo - redo:', redo, 'canRedo:', canRedo);
      }
      return;
    }
    
    // Delete/Backspace - Remove graphic
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (activeObject && activeObject.data?.id && !lockedObjects.has(activeObject.data.id)) {
        e.preventDefault();
        deleteGraphic(activeObject.data.id);
        fabricCanvasRef.current.remove(activeObject);
        fabricCanvasRef.current.requestRenderAll();
        setActiveObject(null);
      }
      return;
    }
    
    // Ctrl+D - Duplicate graphic
    if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
      if (activeObject && activeObject.data?.id) {
        e.preventDefault();
        duplicateGraphic(activeObject.data.id);
      }
      return;
    }
    
    // Arrow keys - Move graphic
    if (activeObject && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const currentLeft = activeObject.left;
      const currentTop = activeObject.top;
      
      let newLeft = currentLeft;
      let newTop = currentTop;
      
      switch (e.key) {
        case 'ArrowLeft':
          newLeft = currentLeft - step;
          break;
        case 'ArrowRight':
          newLeft = currentLeft + step;
          break;
        case 'ArrowUp':
          newTop = currentTop - step;
          break;
        case 'ArrowDown':
          newTop = currentTop + step;
          break;
      }
      
      // Update canvas immediately for smooth movement
      activeObject.set({ left: newLeft, top: newTop });
      activeObject.setCoords();
      fabricCanvasRef.current.requestRenderAll();
      
      // Debounce state update to prevent re-renders during movement
      if (activeObject.data?.id) {
        clearTimeout(keyboardMoveTimeoutRef.current);
        keyboardMoveTimeoutRef.current = setTimeout(() => {
          const canvasPadding = 100;
          updateGraphicPosition(activeObject.data.id, newLeft - canvasPadding, newTop - canvasPadding);
        }, 500); // Update state 500ms after last keypress
      }
      return;
    }
    
    // ? - Show keyboard shortcuts (Shift + / on most keyboards)
    if (e.key === '?') {
      e.preventDefault();
      setShowKeyboardShortcuts(true);
      return;
    }
    
    // Escape - Close keyboard shortcuts modal
    if (e.key === 'Escape') {
      if (showKeyboardShortcutsRef.current) {
        e.preventDefault();
        setShowKeyboardShortcuts(false);
      }
    }
  }, [undo, canUndo, redo, canRedo, lockedObjects, deleteGraphic, duplicateGraphic, updateGraphicPosition, setActiveObject, setShowKeyboardShortcuts]);

  const handleKeyUp = useCallback((e) => {
    // Key up handler if needed
  }, []);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  }, [readOnly]);

  const handleDragOver = useCallback((e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
  }, [readOnly]);

  const handleDragLeave = useCallback((e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    
    // Only set to false if we're leaving the container itself, not a child
    if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingOver(false);
    }
  }, [readOnly]);

  const handleDrop = useCallback(async (e) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Validate file type
    const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      toast.error('Vänligen ladda upp en giltig SVG, PNG eller JPG-fil.');
      return;
    }

    // Use the handleFileCheck function from GraphicsSidebar
    if (handleFileCheck) {
      try {
        await handleFileCheck(file);
      } catch (error) {
        console.error('Error processing file:', error);
        toast.error('Kunde inte bearbeta filen.');
      }
    }
  }, [readOnly, handleFileCheck]);

  // Function to handle view transition
  const handleViewTransition = async () => {
    if (!selectedCarModel || !selectedView) return;
    
    console.log('Starting view transition:', {
      selectedView,
      selectedCarModel,
      currentGraphics: placedGraphics[selectedView]
    });
    
    setIsViewChanging(true);
    
    if (overlayRef.current) {
      overlayRef.current.style.visibility = 'hidden';
    }
    
    // Store existing objects
    const existingObjects = fabricCanvasRef.current.getObjects().filter(obj => 
      obj.type !== 'path'
    );
    
    // Clear the canvas
    fabricCanvasRef.current.clear();

    const img = new Image();
    img.onload = () => {
      console.log('Car image loaded, updating car path');
      updateCarPath();
      
      // Restore objects with new clip path
      existingObjects.forEach(obj => {
        obj.clipPath = carPathRef.current;
        fabricCanvasRef.current.add(obj);
      });
      
      if (overlayRef.current) {
        overlayRef.current.style.visibility = 'visible';
      }
      setIsViewChanging(false);
      
      // Resize canvas to match the loaded image dimensions
      handleResize();
      
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.requestRenderAll();
      }
    };
    img.src = carModelData?.viewImages[selectedView];
  };

  // Create a reusable resize handler function
  const handleResize = () => {
    if (!containerRef.current || !fabricCanvasRef.current) return;
    
    const container = containerRef.current.getBoundingClientRect();
    const canvasPadding = 100;
    
    // Ensure we have a minimum width to prevent too small scaling
    const minWidth = 300;
    const effectiveWidth = Math.max(container.width, minWidth);
    
    // Calculate the scale while maintaining aspect ratio (account for padding)
    const scale = Math.min(
      effectiveWidth / (carDimensions.width + canvasPadding * 2),
      container.height / (carDimensions.height + canvasPadding * 2)
    );

    // Calculate new dimensions that maintain aspect ratio (include padding)
    const scaledWidth = Math.floor((carDimensions.width + canvasPadding * 2) * scale);
    const scaledHeight = Math.floor((carDimensions.height + canvasPadding * 2) * scale);

    // Update canvas dimensions and zoom
    fabricCanvasRef.current.setZoom(scale);
    fabricCanvasRef.current.setDimensions({
      width: scaledWidth,
      height: scaledHeight
    });
    
    // Store canvas dimensions for overlay
    setCanvasDimensions({ width: scaledWidth, height: scaledHeight });
    
    fabricCanvasRef.current.renderAll();
  };

  // Create a function to update the car path
  const updateCarPath = () => {
    if (!fabricCanvasRef.current || !selectedCarModel || !carModelData) return;

    // Store existing graphics
    const existingGraphics = fabricCanvasRef.current.getObjects().filter(obj => 
      obj.type !== 'path' || obj._clipPath
    );

    // Remove old car path
    const oldCarPath = fabricCanvasRef.current.getObjects().find(obj => obj.type === 'path' && !obj._clipPath);
    if (oldCarPath) {
      fabricCanvasRef.current.remove(oldCarPath);
    }

    // Get car model config
    const carConfig = carModelData;
    if (!carConfig.masks || !carConfig.transforms) return;

    const pathString = carConfig.masks[selectedView];
    const transform = carConfig.transforms[selectedView];

    if (!pathString || !transform) return;

    // Add padding offset to center car in padded canvas
    const canvasPadding = 100;

    // Create the car path for clipping with transform
    carPathRef.current = new Path(pathString, {
      absolutePositioned: true,
      left: transform.x + canvasPadding,
      top: transform.y + canvasPadding,
      scaleX: transform.scale,
      scaleY: transform.scale
    });

    // Create the visible car path with transform
    const carPath = new Path(pathString, {
      fill: getCarColorFill(selectedColor, carDimensions.width, carDimensions.height),
      selectable: false,
      evented: false,
      hasControls: false,
      hasBorders: false,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true,
      hoverCursor: 'default',
      left: transform.x + canvasPadding,
      top: transform.y + canvasPadding,
      scaleX: transform.scale,
      scaleY: transform.scale,
      zIndex: 1, // Ensure car path is below graphics
      data: {
        type: 'car',
        nonInteractive: true
      }
    });

    fabricCanvasRef.current.add(carPath);

    // Update clip paths for existing graphics
    existingGraphics.forEach(obj => {
      obj.clipPath = carPathRef.current;
      obj.zIndex = 2;
      // Force a refresh of the object
      obj.dirty = true;
      // Remove and re-add the object to bring it to front
      fabricCanvasRef.current.remove(obj);
      fabricCanvasRef.current.add(obj);
    });

    // Sort objects by z-index
    fabricCanvasRef.current.getObjects().sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
   
    // Force a full canvas re-render
    fabricCanvasRef.current.requestRenderAll();
    fabricCanvasRef.current.renderAll();
  };

  // Initialize Fabric canvas
  useEffect(() => {
    if (canvasRef.current && !fabricCanvasRef.current) {
      // Add padding to canvas size to prevent control clipping
      const canvasPadding = 100; // 100px padding on each side
      fabricCanvasRef.current = new Canvas(canvasRef.current, {
        width: carDimensions.width + (canvasPadding * 2),
        height: carDimensions.height + (canvasPadding * 2),
        preserveObjectStacking: true,
        backgroundColor: 'transparent',
        renderOnAddRemove: true,
      });

      // Set up canvas event listeners
      // Note: object:modified is handled by a separate useEffect below (line ~1130)
      fabricCanvasRef.current.on('object:moving', handleObjectMoving);
      fabricCanvasRef.current.on('object:scaling', handleObjectScaling);
      fabricCanvasRef.current.on('object:rotating', handleObjectRotating);
      fabricCanvasRef.current.on('selection:created', handleSelectionCreated);
      fabricCanvasRef.current.on('selection:cleared', handleSelectionCleared);
      fabricCanvasRef.current.on('mouse:down', handleMouseDown);
      fabricCanvasRef.current.on('mouse:up', handleMouseUp);

      // Initial render
      fabricCanvasRef.current.renderAll();
      
      // Mark canvas as ready so object:modified listener can be registered
      setIsCanvasReady(true);
    }

    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.dispose();
        fabricCanvasRef.current = null;
      }
    };
  }, [canvasRef.current, carDimensions.width, carDimensions.height]);

  // Set up keyboard shortcuts (separate effect so it updates when handlers change)
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [handleKeyDown, handleKeyUp]);

  // Update car path when view changes
  useEffect(() => {
    if (fabricCanvasRef.current) {
      updateCarPath();
      handleResize();
    }
  }, [selectedView]);

  // Resize canvas when car model data is first available
  useEffect(() => {
    if (fabricCanvasRef.current && carModelData && selectedCarModel) {
      // Small delay to ensure the car image has started loading
      setTimeout(() => {
        handleResize();
      }, 100);
    }
  }, [carModelData, selectedCarModel]);

  // Handle window resize
  useEffect(() => {
    let timeoutId;
    const debouncedResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    handleResize(); // Initial resize

    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(timeoutId);
    };
  }, [carDimensions]);

  // Update the useEffect that handles graphics updates
  useEffect(() => {
    if (!fabricCanvasRef.current || !isCanvasReady) return;

    // Add a small delay to ensure canvas has finished resizing
    // This fixes graphics shifting on first load of read-only view
    const timeoutId = setTimeout(() => {
    if (!fabricCanvasRef.current) return;

    // Clear existing objects except car path
    const objects = fabricCanvasRef.current.getObjects();
    const carPath = objects.find(obj => obj.type === 'path' && !obj._clipPath);
    
    // Remove all objects except car path
    objects.forEach(obj => {
      if (obj.type !== 'path') {
        fabricCanvasRef.current.remove(obj);
      }
    });

    // Sort graphics by z-index before adding to canvas
    const sortedGraphics = [...(placedGraphics[selectedView] || [])].sort(
      (a, b) => (a.zIndex || 0) - (b.zIndex || 0)
    );

    // First ensure car path is at the bottom
    if (carPath) {
      fabricCanvasRef.current.remove(carPath);
      fabricCanvasRef.current.add(carPath);
    }

    // Add graphics in order
    const promises = sortedGraphics.map(item => {
      return new Promise((resolve) => {
        if (item.type === 'image') {
          const img = new Image();
          img.onload = () => {
            // Check if the graphic already exists on canvas
            const existingObject = fabricCanvasRef.current.getObjects().find(
              obj => obj.data?.id === item.id
            );

            if (existingObject) {
              existingObject.set({
                left: item.x,
                top: item.y,
                scaleX: item.width / img.width,
                scaleY: item.height / img.height,
                angle: item.rotation || 0,
                flipX: item.flipX
              });
              existingObject.setCoords();
              resolve(existingObject);
            } else {
              const canvasPadding = 100;
              const fabricImage = new FabricImage(img, {
                left: item.x + canvasPadding,
                top: item.y + canvasPadding,
                angle: item.rotation || 0,
                cornerColor: '#FFFFFF',
                cornerStrokeColor: '#000000',
                cornerStyle: 'circle',
                cornerSize: 12,
                cornerStrokeWidth: 2,
                transparentCorners: false,
                lockUniScaling: false,
                name: item.name,
                flipX: item.flipX,
                data: { 
                  id: item.id,
                  view: selectedView
                },
                ...objectConfig,
                clipPath: carPathRef.current,
                perPixelTargetFind: true,
              });

              // Add all controls
              addControlsToObject(fabricImage);

              fabricImage.set({
                scaleX: item.width / img.width,
                scaleY: item.height / img.height
              });

              fabricCanvasRef.current.add(fabricImage);
              resolve(fabricImage);
            }
          };
          img.src = item.src;
        } else if (item.type === 'text') {
          const canvasPadding = 100;
          const text = new IText(item.text, {
            left: item.x + canvasPadding,
            top: item.y + canvasPadding,
            fontFamily: item.font,
            fill: item.color,
            textAlign: item.align,
            angle: item.rotation || 0,
            originX: 'left',
            originY: 'center',
            cornerColor: '#FFFFFF',
            cornerStrokeColor: '#000000',
            cornerStyle: 'circle',
            cornerSize: 12,
            cornerStrokeWidth: 2,
            transparentCorners: false,
            padding: 5,
            selectable: !readOnly,
            evented: !readOnly,
            editable: !readOnly, // Enable inline editing
            data: { 
              id: item.id,
              type: 'text',
              view: selectedView
            },
            ...objectConfig,
            clipPath: carPathRef.current,
            flipX: item.flipX || false
          });

          // Set initial dimensions and handle positioning
          const initialWidth = text.getScaledWidth();
          const initialHeight = text.getScaledHeight();

          if (item.flipX) {
            const carWidth = carDimensions.width;
            const distanceFromRight = carWidth - item.x - initialWidth;
            const mirroredX = distanceFromRight;
            
            text.set({
              left: mirroredX,
              flipX: true,
              originX: 'right'
            });
          }

          if (item.width && item.height) {
            const scaleX = item.width / initialWidth;
            const scaleY = item.height / initialHeight;
            text.set({ scaleX, scaleY });
          }

          text.setCoords();
          addControlsToObject(text);
          fabricCanvasRef.current.add(text);
          resolve(text);
        } else if (item.type === 'shape') {
          const canvasPadding = 100;
          let shape;
          
          const shapeConfig = {
            left: item.x + canvasPadding,
            top: item.y + canvasPadding,
            fill: item.fill || '#000000',
            stroke: item.stroke || '#000000',
            strokeWidth: item.strokeWidth || 2,
            opacity: item.opacity || 1,
            angle: item.rotation || 0,
            cornerColor: '#FFFFFF',
            cornerStrokeColor: '#000000',
            cornerStyle: 'circle',
            cornerSize: 12,
            cornerStrokeWidth: 2,
            transparentCorners: false,
            selectable: !readOnly,
            evented: !readOnly,
            data: { 
              id: item.id,
              type: 'shape',
              shapeType: item.shapeType,
              view: selectedView
            },
            ...objectConfig,
            clipPath: carPathRef.current
          };
          
          switch (item.shapeType) {
            case 'rect':
              shape = new Rect({
                ...shapeConfig,
                width: item.width,
                height: item.height
              });
              break;
            case 'circle':
              shape = new Circle({
                ...shapeConfig,
                radius: Math.min(item.width, item.height) / 2,
                originX: 'center',
                originY: 'center'
              });
              break;
            case 'triangle':
              shape = new Triangle({
                ...shapeConfig,
                width: item.width,
                height: item.height
              });
              break;
            default:
              resolve(null);
              return;
          }
          
          if (shape) {
            addControlsToObject(shape);
            fabricCanvasRef.current.add(shape);
            resolve(shape);
          } else {
            resolve(null);
          }
        }
      });
    });

    // After all graphics are added, select the last one if it's new
    Promise.all(promises).then(addedObjects => {
      if (addedObjects.length > 0) {
        const lastObject = addedObjects[addedObjects.length - 1];
        const lastGraphic = sortedGraphics[sortedGraphics.length - 1];
        
        // Check if this is a newly added graphic (compare timestamps or other criteria)
        const isNewGraphic = Date.now() - lastGraphic.id < 1000; // Within last second
        
        if (isNewGraphic) {
          fabricCanvasRef.current.setActiveObject(lastObject);
          setIsControlActive(true);
          setActiveObject(lastObject);
          updateTooltipPosition(lastObject);
        }
      }
      fabricCanvasRef.current.requestRenderAll();
    });
    }, 50); // 50ms delay to ensure canvas is fully resized

    return () => clearTimeout(timeoutId);
  }, [placedGraphics, selectedView, isCanvasReady]);

  // This object:modified handler is now consolidated into the one below (line ~1116)
  // Removed to prevent conflicts

  // Update car color when selectedColor changes
  useEffect(() => {
    if (!fabricCanvasRef.current) return;
    
    const objects = fabricCanvasRef.current.getObjects();
    const carPath = objects.find(obj => obj.type === 'path' && !obj._clipPath);
    if (carPath) {
      carPath.set('fill', getCarColorFill(selectedColor, carDimensions.width, carDimensions.height));
      fabricCanvasRef.current.renderAll();
    }
  }, [selectedColor, carDimensions.width, carDimensions.height]);

  // Shape drawing mode
  useEffect(() => {
    if (!fabricCanvasRef.current || readOnly) return;
    
    const canvas = fabricCanvasRef.current;
    
    if (activeShapeTool) {
      // Enable drawing mode
      canvas.selection = false;
      canvas.forEachObject((obj) => {
        // Make all objects non-selectable in drawing mode
        obj.selectable = false;
        obj.evented = false;
      });
      
      const handleShapeMouseDown = (e) => {
        // Don't start drawing if clicking on an object (except the car path)
        if (!activeShapeTool) return;
        if (e.target && e.target.data?.type !== 'car') return;
        
        const pointer = canvas.getScenePoint(e.e);
        setIsDrawingShape(true);
        setDrawingStartPoint({ x: pointer.x, y: pointer.y });
      };
      
      const handleShapeMouseMove = (e) => {
        if (!isDrawingShape || !drawingStartPoint) return;
        
        const pointer = canvas.getScenePoint(e.e);
        
        // Remove previous temp shape
        if (tempShape) {
          canvas.remove(tempShape);
        }
        
        // Create preview shape
        let shape;
        const startX = drawingStartPoint.x;
        const startY = drawingStartPoint.y;
        const width = Math.abs(pointer.x - startX);
        const height = Math.abs(pointer.y - startY);
        const left = Math.min(startX, pointer.x);
        const top = Math.min(startY, pointer.y);
        
        const shapeConfig = {
          left,
          top,
          fill: shapeProperties.fill,
          stroke: shapeProperties.stroke,
          strokeWidth: shapeProperties.strokeWidth,
          opacity: shapeProperties.opacity,
          selectable: false,
          evented: false,
          clipPath: carPathRef.current
        };
        
        switch (activeShapeTool) {
          case 'rect':
            shape = new Rect({
              ...shapeConfig,
              width,
              height
            });
            break;
          case 'circle':
            shape = new Circle({
              ...shapeConfig,
              radius: Math.min(width, height) / 2,
              left: startX,
              top: startY,
              originX: 'center',
              originY: 'center'
            });
            break;
          case 'triangle':
            shape = new Triangle({
              ...shapeConfig,
              width,
              height
            });
            break;
          default:
            break;
        }
        
        if (shape) {
          canvas.add(shape);
          setTempShape(shape);
          canvas.renderAll();
        }
      };
      
      const handleShapeMouseUp = (e) => {
        if (!isDrawingShape || !drawingStartPoint) return;
        
        const pointer = canvas.getScenePoint(e.e);
        
        // Remove temp shape
        if (tempShape) {
          canvas.remove(tempShape);
          setTempShape(null);
        }
        
        // Calculate final dimensions
        const startX = drawingStartPoint.x;
        const startY = drawingStartPoint.y;
        const width = Math.abs(pointer.x - startX);
        const height = Math.abs(pointer.y - startY);
        
        // Only create shape if it has meaningful size
        if (width > 5 && height > 5) {
          const shapeId = Date.now();
          const left = Math.min(startX, pointer.x);
          const top = Math.min(startY, pointer.y);
          
          // Subtract canvas padding to get car-relative coordinates
          const canvasPadding = 100;
          
          // Create shape data for state
          const shapeData = {
            id: shapeId,
            type: 'shape',
            shapeType: activeShapeTool,
            x: left - canvasPadding,
            y: top - canvasPadding,
            width,
            height,
            rotation: 0,
            fill: shapeProperties.fill,
            stroke: shapeProperties.stroke,
            strokeWidth: shapeProperties.strokeWidth,
            opacity: shapeProperties.opacity
          };
          
          // Add to placed graphics via callback
          if (placeShape) {
            placeShape(shapeData);
          }
          
          // Notify parent to deactivate shape tool (for single-draw UX)
          if (onShapeSelect) {
            // This will signal to deactivate the tool after drawing
            setTimeout(() => {
              onShapeSelect({ justDrawn: true, ...shapeData });
            }, 100);
          }
        }
        
        setIsDrawingShape(false);
        setDrawingStartPoint(null);
        canvas.renderAll();
      };
      
      canvas.on('mouse:down', handleShapeMouseDown);
      canvas.on('mouse:move', handleShapeMouseMove);
      canvas.on('mouse:up', handleShapeMouseUp);
      
      return () => {
        canvas.off('mouse:down', handleShapeMouseDown);
        canvas.off('mouse:move', handleShapeMouseMove);
        canvas.off('mouse:up', handleShapeMouseUp);
      };
    } else {
      // Restore normal selection mode
      canvas.selection = true;
      canvas.forEachObject((obj) => {
        // Make graphics selectable, but never the car
        if (obj.data?.type === 'car') {
          obj.selectable = false;
          obj.evented = false;
        } else {
          obj.selectable = !readOnly;
          obj.evented = !readOnly;
        }
      });
    }
  }, [activeShapeTool, isDrawingShape, drawingStartPoint, tempShape, shapeProperties, readOnly, placeShape]);

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    addText: async (textConfig) => {
      console.log('Adding new text:', textConfig);
      if (!fabricCanvasRef.current) return;

      const textId = Date.now();
      const x = carDimensions.width / 2;
      const y = carDimensions.height / 2;
      
      // Create the text object for state first
      const textObject = {
        id: textId,
        text: textConfig.text,
        font: textConfig.font,
        color: textConfig.color,
        align: textConfig.align,
        x: x,
        y: y,
        rotation: 0,
        type: 'text',
        width: 0,
        height: 0
      };

      // Create the fabric text object
      const canvasPadding = 100;
      const text = new Text(textConfig.text, {
        left: x + canvasPadding,
        top: y + canvasPadding,
        fontFamily: textConfig.font,
        fill: textConfig.color,
        textAlign: textConfig.align,
        originX: 'center',
        originY: 'center',
        cornerColor: '#FFFFFF',
        cornerStrokeColor: '#000000',
        cornerStyle: 'circle',
        cornerSize: 12,
        cornerStrokeWidth: 2,
        transparentCorners: false,
        padding: 5,
        data: { 
          id: textId,
          type: 'text'
        },
        ...objectConfig,
        clipPath: carPathRef.current
      });

      // Update dimensions
      textObject.width = text.width;
      textObject.height = text.height;

      // Add to state and canvas
      await placeText(textObject);
      fabricCanvasRef.current.add(text);
      fabricCanvasRef.current.setActiveObject(text);
      fabricCanvasRef.current.requestRenderAll();

      // Don't automatically select for editing
      onTextSelect(null);
    },
    deselectActive: () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.discardActiveObject();
        fabricCanvasRef.current.renderAll();
        setActiveObject(null);
        if (onTextSelect) {
          onTextSelect(null);
        }
      }
    },
    updateSelectedShapeProperties: (properties) => {
      if (!fabricCanvasRef.current) return;
      
      const activeObj = fabricCanvasRef.current.getActiveObject();
      if (activeObj && activeObj.data?.type === 'shape') {
        // Update visual properties (only fill now)
        if (properties.fill !== undefined) {
          activeObj.set('fill', properties.fill);
        }
        
        fabricCanvasRef.current.renderAll();
        
        // Update state
        setPlacedGraphics(prev => {
          const viewGraphics = prev[selectedView] || [];
          return {
            ...prev,
            [selectedView]: viewGraphics.map(item =>
              item.id === activeObj.data.id
                ? { ...item, ...properties }
                : item
            )
          };
        });
      }
    }
  }));

  // Modify object creation to be non-selectable when readOnly
  const getObjectConfig = (isReadOnly) => ({
    selectable: !isReadOnly,
    hasControls: !isReadOnly,
    hasBorders: !isReadOnly,
    lockMovementX: isReadOnly,
    lockMovementY: isReadOnly,
    hasRotatingPoint: true, // Enable rotation
    showMidPoints: !isReadOnly,
    transparentCorners: false,
    cornerStyle: 'circle',
    cornerColor: '#FFFFFF',
    cornerStrokeColor: '#000000',
    cornerSize: 12,
    cornerStrokeWidth: 2,
    // Border styling - solid black line
    borderColor: '#000000',
    borderDashArray: null, // Solid line instead of dashed
    borderScaleFactor: 1.5,
    // Control visibility
    setControlVisible: {
      ml: !isReadOnly,
      mr: !isReadOnly,
      mt: !isReadOnly,
      mb: !isReadOnly,
      mtr: true, // Enable rotation control but hide the visual
    }
  });

  // Use this config when creating objects
  const objectConfig = getObjectConfig(readOnly);

  // Add z-index controls to objects
  const addControlsToObject = (fabricObject, item) => {
    // Modify the rotation control with rotation icon
    if (fabricObject.controls.mtr) {
      fabricObject.controls.mtr.render = function(ctx, left, top, styleOverride, fabricObject) {
        const size = 12;
        
        ctx.save();
        ctx.translate(left, top);
        
        // Draw white circle with black border
        ctx.beginPath();
        ctx.arc(0, 0, size, 0, 2 * Math.PI);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Draw simple 3/4 circle icon
        const iconRadius = size * 0.55;
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1.5;
        ctx.lineCap = 'round';

        // Draw 3/4 circular arc (270 degrees)
        ctx.beginPath();
        ctx.arc(0, 0, iconRadius, Math.PI * 0.25, Math.PI * 2, false);
        ctx.stroke();

        ctx.restore();
      };
      fabricObject.controls.mtr.cornerSize = 12;
      fabricObject.controls.mtr.touchSizeX = 32;
      fabricObject.controls.mtr.touchSizeY = 32;
      fabricObject.controls.mtr.offsetY = -35;
      fabricObject.controls.mtr.offsetX = 0;
    }

    // Remove the delete control if it exists
    delete fabricObject.controls.deleteControl;
    // Remove the layer controls
    delete fabricObject.controls.toFrontControl;
    delete fabricObject.controls.toBackControl;
  };

  // Add this near the top of the component
  useEffect(() => {
    logger.log('Current car model:', selectedCarModel);
    logger.log('Car model config:', carModelData);
    if (selectedCarModel && carModelData) {
      logger.log('SVG path:', `${carModelData.viewImages[selectedView]}`);
      logger.log('Background SVG path:', `/images/cars/${selectedCarModel}/${selectedView.toLowerCase().replace(' ', '-')}.svg`);
    }
  }, [selectedCarModel, selectedView]);

  // Update the useEffect that handles car model changes
  useEffect(() => {
    if (selectedCarModel && carModelData) {
      // Preload assets when car model changes - convert single model to object format
      const modelsObject = { [selectedCarModel]: carModelData };
      preloadCarAssets(selectedCarModel, modelsObject);
      
      // Initialize canvas after preloading
      if (fabricCanvasRef.current) {
        updateCarPath();
        handleResize();
      }
    }
  }, [selectedCarModel, carModelData]);

  // Effect to handle view changes
  useEffect(() => {
    handleViewTransition();
  }, [selectedView, selectedCarModel]);

  // Clean up event listeners when component unmounts
  useEffect(() => {
    if (fabricCanvasRef.current) {
      const canvas = fabricCanvasRef.current;
      
      const handleMoving = () => setIsControlActive(true);
      const handleScaling = () => setIsControlActive(true);
      const handleRotating = () => setIsControlActive(true);
      const handleSelectionCreated = () => setIsControlActive(true);
      const handleSelectionUpdated = () => setIsControlActive(true);
      const handleMouseUp = () => {
        if (!canvas.getActiveObject()) {
          setIsControlActive(false);
        }
      };
      const handleSelectionCleared = () => setIsControlActive(false);
      
      // Handle when user finishes editing text (exits edit mode)
      const handleTextEditingExited = (e) => {
        const target = e.target;
        
        if (target && target.type === 'i-text' && target.data?.id) {
          // Update the text in state only when editing is finished
          setPlacedGraphics(prev => {
            const view = target.data.view || selectedView;
            const updatedGraphics = prev[view].map(item => {
              if (item.id === target.data.id) {
                return {
                  ...item,
                  text: target.text
                };
              }
              return item;
            });
            
            return {
              ...prev,
              [view]: updatedGraphics
            };
          });
        }
      };

      canvas.on('object:moving', handleMoving);
      canvas.on('object:scaling', handleScaling);
      canvas.on('object:rotating', handleRotating);
      canvas.on('selection:created', handleSelectionCreated);
      canvas.on('selection:updated', handleSelectionUpdated);
      canvas.on('mouse:up', handleMouseUp);
      canvas.on('selection:cleared', handleSelectionCleared);
      canvas.on('text:editing:exited', handleTextEditingExited);

      return () => {
        // Clean up all event listeners
        canvas.off('object:moving', handleMoving);
        canvas.off('object:scaling', handleScaling);
        canvas.off('object:rotating', handleRotating);
        canvas.off('selection:created', handleSelectionCreated);
        canvas.off('selection:updated', handleSelectionUpdated);
        canvas.off('mouse:up', handleMouseUp);
        canvas.off('selection:cleared', handleSelectionCleared);
        canvas.off('text:editing:exited', handleTextEditingExited);
      };
    }
  }, [fabricCanvasRef.current]);

  // Update the tooltip positioning function
  const updateTooltipPosition = (obj) => {
    if (!obj || !canvasRef.current || !fabricCanvasRef.current) return;
    
    // Get the HTML canvas element's position on screen
    const canvasElement = canvasRef.current;
    const canvasRect = canvasElement.getBoundingClientRect();
    const zoom = fabricCanvasRef.current.getZoom();
    
    // Get object bounds in canvas coordinates
    const objBounds = obj.getBoundingRect(false, true);
    
    // Convert canvas coordinates to screen coordinates
    // Canvas coordinates are in the zoomed canvas space
    const canvasLeft = objBounds.left * zoom;
    const canvasTop = objBounds.top * zoom;
    const canvasWidth = objBounds.width * zoom;
    const canvasHeight = objBounds.height * zoom;
    
    // Calculate screen position (canvas position + object position within canvas)
    const screenX = canvasRect.left + canvasLeft + (canvasWidth / 2);
    const screenY = canvasRect.top + canvasTop + canvasHeight;
    
    // Add corner control size + 16px margin
    const x = screenX;
    const y = screenY + 12 + 16; // 12px corner control + 16px margin
    
    setTooltipPosition({ x, y });
  };

  // Update canvas initialization to handle selection
  useEffect(() => {
    if (canvasRef.current && !fabricCanvasRef.current) {
      // ... existing canvas initialization code

      fabricCanvasRef.current.on('selection:created', (e) => {
        const selectedObject = e.selected[0];
        setIsControlActive(true);
        setActiveObject(selectedObject);
        updateTooltipPosition(selectedObject);
      });

      fabricCanvasRef.current.on('selection:updated', (e) => {
        const selectedObject = e.selected[0];
        setActiveObject(selectedObject);
        updateTooltipPosition(selectedObject);
      });

      fabricCanvasRef.current.on('selection:cleared', () => {
        setIsControlActive(false);
        setActiveObject(null);
        if (onTextSelect) {
          onTextSelect(null);
        }
      });

      fabricCanvasRef.current.on('object:moving', (e) => {
        updateTooltipPosition(e.target);
      });

      fabricCanvasRef.current.on('object:scaling', (e) => {
        updateTooltipPosition(e.target);
      });

      fabricCanvasRef.current.on('object:rotating', (e) => {
        updateTooltipPosition(e.target);
      });
    }
  }, []);

  // Add function to handle locking/unlocking
  const toggleLock = (obj) => {
    if (!obj) return;
    
    const isLocked = lockedObjects.has(obj.data.id);
    const newLockedObjects = new Set(lockedObjects);
    
    if (isLocked) {
      newLockedObjects.delete(obj.data.id);
      obj.set({
        selectable: true,
        hasControls: true,
        lockMovementX: false,
        lockMovementY: false,
        lockRotation: false,
        lockScalingX: false,
        lockScalingY: false
      });
    } else {
      newLockedObjects.add(obj.data.id);
      obj.set({
        selectable: true,
        hasControls: false,
        lockMovementX: true,
        lockMovementY: true,
        lockRotation: true,
        lockScalingX: true,
        lockScalingY: true
      });
    }
    
    setLockedObjects(newLockedObjects);
    fabricCanvasRef.current.requestRenderAll();
  };

  // Update the handleDuplicate function
  const handleDuplicate = (obj) => {
    if (!obj) return;
    
    const newId = Date.now();
    
    if (obj.type === 'i-text') {
      // Handle text object duplication
      const newText = new IText(obj.text, {
        left: obj.left + 20,
        top: obj.top + 20,
        fontFamily: obj.fontFamily,
        fill: obj.fill,
        textAlign: obj.textAlign,
        angle: obj.angle || 0,
        originX: obj.originX,
        originY: obj.originY,
        cornerColor: obj.cornerColor,
        cornerStrokeColor: obj.cornerStrokeColor,
        cornerStyle: obj.cornerStyle,
        cornerStrokeWidth: obj.cornerStrokeWidth,
        transparentCorners: obj.transparentCorners,
        padding: obj.padding,
        scaleX: obj.scaleX,
        scaleY: obj.scaleY,
        editable: true, // Enable inline editing
        data: { 
          id: newId,
          type: 'text',
          view: selectedView
        },
        ...objectConfig,
        clipPath: carPathRef.current
      });
      
      fabricCanvasRef.current.add(newText);
      fabricCanvasRef.current.setActiveObject(newText);
      fabricCanvasRef.current.requestRenderAll();
      
      duplicateGraphic(obj.data.id, newId, selectedView);
    } else {
      // Handle image object duplication
      const img = new Image();
      img.onload = () => {
        const fabricImage = new FabricImage(img, {
          left: obj.left + 20,
          top: obj.top + 20,
          angle: obj.angle || 0,
          scaleX: obj.scaleX,
          scaleY: obj.scaleY,
          cornerColor: obj.cornerColor,
          cornerStrokeColor: obj.cornerStrokeColor,
          cornerStyle: obj.cornerStyle,
          cornerStrokeWidth: obj.cornerStrokeWidth,
          transparentCorners: obj.transparentCorners,
          data: { 
            ...obj.data,
            id: newId
          },
          ...objectConfig,
          clipPath: carPathRef.current,
          perPixelTargetFind: true,
        });

        // Add controls to the duplicated image
        addControlsToObject(fabricImage);
        
        fabricCanvasRef.current.add(fabricImage);
        fabricCanvasRef.current.setActiveObject(fabricImage);
        fabricCanvasRef.current.requestRenderAll();
        
        duplicateGraphic(obj.data.id, newId, selectedView);
      };
      img.src = obj.getSrc();
    }
  };

  // Add click handler for context menu
  const handleContextMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  // Add click handler to close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMenu) {
        setShowMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMenu]);

  // Add this function to calculate real dimensions
  const calculateRealDimensions = (fabricObject) => {
    if (!fabricObject || !selectedCarModel) return { width: 0, height: 0 };

    // Get the real dimensions for the current view
    const viewDimensions = carModelData.dimensions[selectedView];
    if (!viewDimensions) return { width: 0, height: 0 };

    // Get object dimensions in canvas units
    const canvasWidth = fabricObject.getScaledWidth();
    const canvasHeight = fabricObject.getScaledHeight();

    // We need to account for the car's scale in the view
    const carTransform = carModelData.transforms[selectedView];
    const carScale = carTransform.scale || 1;

    // Calculate the scale factors between canvas and real dimensions
    // When car is scaled up (carScale > 1), each canvas pixel represents less real distance
    // When car is scaled down (carScale < 1), each canvas pixel represents more real distance
    const scaleX = viewDimensions.realWidth / (carDimensions.width / carScale);
    const scaleY = viewDimensions.realHeight / (carDimensions.height / carScale);

    // Calculate real dimensions in millimeters
    const realWidth = Math.round(canvasWidth * scaleX);
    const realHeight = Math.round(canvasHeight * scaleY);

    return {
      width: realWidth,
      height: realHeight,
      unit: 'mm'
    };
  };

  // Update the object:modified event to include real dimensions, position, and rotation
  useEffect(() => {
    if (!fabricCanvasRef.current || !isCanvasReady) {
      return;
    }

    const handleModified = (e) => {
      const obj = e.target;
      if (!obj || !obj.data) {
        return;
      }

      const canvasPadding = 100;
      const width = obj.getScaledWidth();
      const height = obj.getScaledHeight();
      let newX = obj.left - canvasPadding;
      let newY = obj.top - canvasPadding;

      // For mirrored objects, adjust the position relative to car width
      if (obj.flipX && obj.data.type === 'text') {
        const carWidth = carDimensions.width;
        const objectWidth = width;
        const distanceFromRight = carWidth - (obj.left - canvasPadding) - objectWidth;
        newX = distanceFromRight;
        
        // Update origin based on flip state
        obj.set({
          originX: 'right'
        });
      } else if (obj.data.type === 'text') {
        obj.set({
          originX: 'left'
        });
      }

      // Calculate real dimensions inline to avoid stale closure
      let realDimensions = { width: 0, height: 0 };
      
      if (obj && selectedCarModel && carModelData) {
        const viewDimensions = carModelData.dimensions?.[selectedView];
        if (viewDimensions) {
          const canvasWidth = obj.getScaledWidth();
          const canvasHeight = obj.getScaledHeight();
          const carTransform = carModelData.transforms?.[selectedView];
          const carScale = carTransform?.scale || 1;
          const scaleX = viewDimensions.realWidth / (carDimensions.width / carScale);
          const scaleY = viewDimensions.realHeight / (carDimensions.height / carScale);
          realDimensions = {
            width: Math.round(canvasWidth * scaleX),
            height: Math.round(canvasHeight * scaleY),
            unit: 'mm'
          };
        }
      }
      
      // Update position (subtract padding to get original car-relative position)
      updateGraphicPosition(obj.data.id, newX, newY);
      
      // Update rotation
      updateGraphicRotation(obj.data.id, obj.angle || 0);
      
      // Update size with real dimensions (this triggers price calculation)
      updateGraphicSize(
        obj.data.id, 
        obj.getScaledWidth(), 
        obj.getScaledHeight(), 
        realDimensions
      );

      // Update flipX state directly
      setPlacedGraphics(prev => {
        const viewGraphics = [...prev[selectedView]];
        const index = viewGraphics.findIndex(g => g.id === obj.data.id);
        
        if (index !== -1) {
          viewGraphics[index] = {
            ...viewGraphics[index],
            flipX: obj.flipX || false
          };
        }

        return {
          ...prev,
          [selectedView]: viewGraphics
        };
      });
    };

    fabricCanvasRef.current.on('object:modified', handleModified);

    // Cleanup: remove event listener on unmount or when dependencies change
    return () => {
      if (fabricCanvasRef.current) {
        fabricCanvasRef.current.off('object:modified', handleModified);
      }
    };
  }, [isCanvasReady, selectedView, selectedCarModel, carModelData, carDimensions, updateGraphicSize, updateGraphicPosition, updateGraphicRotation, setPlacedGraphics]);

  // Add this helper function to convert mm to cm with 1 decimal place
  const formatDimension = (mm) => {
    return (mm / 10).toFixed(1);
  };

  // Add this helper function to determine if we're on a side view
  const isSideView = (view) => view === 'Sida Vänster' || view === 'Sida Höger';

  // Add this helper to get the opposite side
  const getOppositeSide = (view) => {
    if (view === 'Sida Vänster') return 'Sida Höger';
    if (view === 'Sida Höger') return 'Sida Vänster';
    return null;
  };

  // Update the mirror function
  const handleMirrorToOtherSide = async (obj) => {
    if (!obj || !isSideView(selectedView)) return;
    
    const oppositeSide = getOppositeSide(selectedView);
    if (!oppositeSide) return;

    try {
      // Find the original graphic in state
      const originalGraphic = placedGraphics[selectedView].find(g => g.id === obj.data.id);
      if (!originalGraphic) return;

      // Create a new ID for the copied graphic
      const newId = Date.now();

      // Calculate the position relative to the car's length
      const totalWidth = carDimensions.width;
      const graphicWidth = originalGraphic.width;
      
      console.log('Position calculation:', {
        totalWidth,
        graphicWidth,
        originalX: originalGraphic.x,
        originalY: originalGraphic.y
      });

      // Calculate the mirrored X position
      const newX = totalWidth - (originalGraphic.x + graphicWidth);

      console.log('New position:', {
        originalX: originalGraphic.x,
        newX,
        originalY: originalGraphic.y
      });

      // Create the copied graphic with mirrored position
      const copiedGraphic = {
        ...originalGraphic,
        id: newId,
        x: newX,
        y: originalGraphic.y
      };

      // Update state with the copied graphic
      setPlacedGraphics(prev => ({
        ...prev,
        [oppositeSide]: [...(prev[oppositeSide] || []), copiedGraphic]
      }));

      // Switch to the opposite side view
      setSelectedView(oppositeSide);

      // Clear any active selection
      setActiveObject(null);
      setIsControlActive(false);

    } catch (error) {
      console.error('Error during copying:', error);
    }
  };

  // Add this useEffect to monitor placedGraphics changes
  useEffect(() => {
    console.log('placedGraphics updated:', {
      currentView: selectedView,
      allGraphics: placedGraphics,
      viewGraphics: placedGraphics[selectedView]
    });
  }, [placedGraphics, selectedView]);

  // Add this function near other handler functions
  const handleFlip = (obj) => {
    if (!obj) return;

    // Store original position and dimensions
    const originalLeft = obj.left;
    const originalTop = obj.top;
    const originalWidth = obj.getScaledWidth();

    // Handle text objects differently
    if (obj.type === 'i-text') {
      // For text objects, calculate the new position based on current flip state
      const currentFlipX = obj.flipX;
      const newFlipX = !currentFlipX;
      
      // Calculate new position based on flip direction
      let newLeft;
      if (currentFlipX) {
        // If currently flipped, going back to normal
        newLeft = originalLeft - originalWidth;
      } else {
        // If not flipped, going to flipped state
        newLeft = originalLeft + originalWidth;
      }

      obj.set({
        flipX: newFlipX,
        left: newLeft
      });
    } else {
      // For images and other objects, maintain center point
      const center = obj.getCenterPoint();
      
      obj.set({
        flipX: !obj.flipX
      });

      // Calculate new position to maintain center point
      const newCenter = obj.getCenterPoint();
      const deltaX = center.x - newCenter.x;
      const deltaY = center.y - newCenter.y;

      obj.set({
        left: obj.left + deltaX,
        top: obj.top + deltaY
      });
    }

    // Force controls to update
    if (fabricCanvasRef.current) {
      fabricCanvasRef.current.setActiveObject(obj);
    }

    // Update state
    setPlacedGraphics(prev => {
      const viewGraphics = [...prev[selectedView]];
      const index = viewGraphics.findIndex(g => g.id === obj.data.id);
      
      if (index !== -1) {
        viewGraphics[index] = {
          ...viewGraphics[index],
          flipX: obj.flipX,
          x: obj.left,
          y: obj.top
        };
      }

      return {
        ...prev,
        [selectedView]: viewGraphics
      };
    });

    // Ensure coordinates are updated
    obj.setCoords();
    fabricCanvasRef.current.requestRenderAll();
  };

  // Add these handler functions
  const handleZIndexChange = (obj, direction) => {
    if (!obj || !fabricCanvasRef.current) {
      console.log('No object or canvas:', { obj, canvas: !!fabricCanvasRef.current });
      return;
    }

    const canvas = fabricCanvasRef.current;
    const objects = canvas.getObjects();
    const currentIndex = objects.indexOf(obj);
    
    console.log('Z-index change:', {
      direction,
      currentIndex,
      totalObjects: objects.length,
      objectType: obj.type,
      objectId: obj.data?.id,
      allObjects: objects.map(o => ({ type: o.type, id: o.data?.id }))
    });
    
    if (direction === 1 && currentIndex < objects.length - 1) {
      // Moving up - swap with next object
      const nextObject = objects[currentIndex + 1];
      console.log('Moving up:', {
        nextObjectType: nextObject?.type,
        nextObjectId: nextObject?.data?.id,
        willSwap: nextObject?.type !== 'path'
      });

      if (nextObject?.type !== 'path') {
        // Store the objects' positions
        const objLeft = obj.left;
        const objTop = obj.top;
        const nextLeft = nextObject.left;
        const nextTop = nextObject.top;

        // Remove both objects
        canvas.remove(obj);
        canvas.remove(nextObject);

        // Add them back in swapped order with their original positions
        nextObject.set({ left: nextLeft, top: nextTop });
        obj.set({ left: objLeft, top: objTop });
        
        canvas.add(nextObject);
        canvas.add(obj);
        console.log('Swapped objects up');
      }
    } else if (direction === -1 && currentIndex > 1) {
      // Moving down - swap with previous object
      const prevObject = objects[currentIndex - 1];
      console.log('Moving down:', {
        prevObjectType: prevObject?.type,
        prevObjectId: prevObject?.data?.id,
        willSwap: prevObject?.type !== 'path'
      });

      if (prevObject?.type !== 'path') {
        // Store the objects' positions
        const objLeft = obj.left;
        const objTop = obj.top;
        const prevLeft = prevObject.left;
        const prevTop = prevObject.top;

        // Remove both objects
        canvas.remove(obj);
        canvas.remove(prevObject);

        // Add them back in swapped order with their original positions
        obj.set({ left: objLeft, top: objTop });
        prevObject.set({ left: prevLeft, top: prevTop });
        
        canvas.add(obj);
        canvas.add(prevObject);
        console.log('Swapped objects down');
      }
    }

    // Make sure the car path stays at the bottom
    const carPath = objects.find(o => o.type === 'path');
    if (carPath) {
      const pathLeft = carPath.left;
      const pathTop = carPath.top;
      canvas.remove(carPath);
      carPath.set({ left: pathLeft, top: pathTop });
      canvas.add(carPath);
      console.log('Moved car path to bottom');
    }

    // Update state
    updateGraphicZIndex(obj.data.id, direction === 1 ? 'forward' : 'backward');
    console.log('Updated graphic z-index in state');

    // Set the active object again and preserve its position
    canvas.setActiveObject(obj);
    canvas.requestRenderAll();
    setShowMenu(false);
    
    // Log final state
    console.log('Final canvas objects:', canvas.getObjects().map(o => ({
      type: o.type,
      id: o.data?.id,
      position: { left: o.left, top: o.top }
    })));
  };

  const handleDelete = (obj) => {
    if (!obj || lockedObjects.has(obj.data.id)) return;
    
    deleteGraphic(obj.data.id);
    fabricCanvasRef.current.remove(obj);
    fabricCanvasRef.current.requestRenderAll();
    setActiveObject(null);
  };

  // Update the tooltip JSX
  return (
    <div className="car-preview-container" ref={ref}>
      <div 
        ref={containerRef} 
        className={`car-preview-canvas-container relative w-full h-full aspect-video box-generic-car-preview mt-2 overflow-visible flex items-center justify-center bg-secondary/50 ${
          isControlActive ? 'active-control' : ''
        } ${isViewChanging ? 'loading-side' : ''} ${
          selectedView ? selectedView.toLowerCase().replace(/\s+/g, '-').replace(/ä/g, 'a').replace(/ö/g, 'o') : ''
        }`}
        style={{ 
          minHeight: '300px',
          backgroundColor: '#212025',
          position: 'relative'
        }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Add car model info */}
        {selectedCarModel && carModelData && (
          <div className="absolute top-4 left-4 z-30 flex items-center gap-3 chosen-car-model-info">
            {onBackToCarSelection && (
              <Button 
                variant="secondary" 
                size="icon"
                onClick={onBackToCarSelection}
                className="h-10 w-10"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              <div className="text-sm text-muted-foreground">{carModelData.brand}</div>
              <div className="text-lg font-semibold text-foreground -mt-1">{carModelData.name}</div>
            </div>
          </div>
        )}

        <canvas 
          ref={canvasRef} 
          className="car-preview-canvas"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 10
          }}
        />
        
        {selectedCarModel && carModelData && (
          <canvas
            ref={overlayRef}
            className="car-preview-overlay absolute top-0 left-0 object-contain pointer-events-none"
            style={{ 
              zIndex: 200,
              visibility: isViewChanging ? 'hidden' : 'visible'
            }}
            onContextMenu={(e) => e.preventDefault()}
          />
        )}

        {/* Loading spinner */}
        {isViewChanging && (
          <div className="absolute z-30 inset-0 flex items-center justify-center bg-secondary/50">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        )}

        {/* Drag and drop overlay */}
        {isDraggingOver && !readOnly && (
          <div className="absolute inset-0 z-[250] pointer-events-none animate-in fade-in duration-200">
            <div className="absolute inset-0 bg-primary/10 backdrop-blur-sm border-4 border-dashed border-primary rounded-lg flex items-center justify-center">
              <div className="bg-background/95 rounded-xl px-8 py-6 shadow-2xl border-2 border-primary flex flex-col items-center gap-4 animate-in zoom-in-95 duration-200">
                <div className="bg-primary/20 p-4 rounded-full upload-icon">
                  <Upload className="w-12 h-12 text-primary" />
                </div>
                <div className="text-center">
                  <p className="text-xl font-semibold text-foreground mb-1">
                    Släpp för att ladda upp
                  </p>
                  <p className="text-sm text-muted-foreground">
                    SVG, PNG eller JPG
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Updated Tooltip with size info and actions */}
        {activeObject && (
          <div
            className="tooltip-graphic-info fixed z-[100] bg-background border border-border rounded-lg shadow-md px-3 py-2.5 flex flex-col gap-3 pointer-events-auto"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              transform: 'translateX(-50%) translateY(-60%)', // Center horizontally only
            }}
          >
            {/* Size information */}
            <div className="text-xs">
              <div className="font-semibold text-foreground mb-1.5">
                {activeObject.type === 'i-text' ? 'Text' : truncateFilename(activeObject.name || 'Grafik')}
              </div>
              <div className="flex gap-3 text-muted-foreground">
                {(() => {
                  const realDimensions = calculateRealDimensions(activeObject);
                  return (
                    <>
                      <span>Bredd: {formatDimension(realDimensions.width)} cm</span>
                      <span>Höjd: {formatDimension(realDimensions.height)} cm</span>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-border w-full"></div>

            {/* Action buttons */}
            <TooltipProvider delayDuration={300}>
              <div className="flex items-center gap-2">
                {/* Lock/Unlock button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => toggleLock(activeObject)}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      {lockedObjects.has(activeObject.data.id) ? (
                        <Lock size={16} className="text-foreground" />
                      ) : (
                        <Unlock size={16} className="text-foreground" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{lockedObjects.has(activeObject.data.id) ? "Lås upp" : "Lås"}</p>
                  </TooltipContent>
                </Tooltip>

                {/* Duplicate button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => handleDuplicate(activeObject)}
                      className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${
                        lockedObjects.has(activeObject.data.id)
                          ? 'text-muted-foreground cursor-not-allowed opacity-50'
                          : 'hover:bg-accent hover:text-accent-foreground'
                      }`}
                      disabled={lockedObjects.has(activeObject.data.id)}
                    >
                      <Copy size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Duplicera</p>
                  </TooltipContent>
                </Tooltip>

                {/* Mirror button */}
                {isSideView(selectedView) && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                          handleMirrorToOtherSide(activeObject);
                          setShowMenu(false);
                        }}
                        className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${
                          lockedObjects.has(activeObject.data.id)
                            ? 'text-muted-foreground cursor-not-allowed opacity-50'
                            : 'hover:bg-accent hover:text-accent-foreground'
                        }`}
                        disabled={lockedObjects.has(activeObject.data.id)}
                      >
                        <FlipHorizontal size={16} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Spegla till andra sidan</p>
                    </TooltipContent>
                  </Tooltip>
                )}

                {/* Delete button */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        if (!lockedObjects.has(activeObject.data.id)) {
                          deleteGraphic(activeObject.data.id);
                          fabricCanvasRef.current.remove(activeObject);
                          fabricCanvasRef.current.requestRenderAll();
                          setActiveObject(null);
                        }
                      }}
                      className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${
                        lockedObjects.has(activeObject.data.id)
                          ? 'text-destructive/50 cursor-not-allowed opacity-50'
                          : 'text-destructive hover:bg-destructive/10'
                      }`}
                      disabled={lockedObjects.has(activeObject.data.id)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Ta bort</p>
                  </TooltipContent>
                </Tooltip>

                {/* Divider */}
                <div className="w-px h-5 bg-border"></div>

                {/* More options (kebab) menu */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleContextMenuClick}
                      className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <MoreVertical size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Fler alternativ</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          </div>
        )}

        {/* Rotation Tooltip */}
        {showRotationTooltip && (
          <div
            className={`absolute z-40 pointer-events-none flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium shadow-md border transition-colors ${
              isRotationSnapping 
                ? 'bg-primary text-primary-foreground border-primary' 
                : 'bg-background text-foreground border-border'
            }`}
            style={{
              left: rotationTooltipPosition.x,
              top: rotationTooltipPosition.y,
              transform: 'translate(-50%, -50%)',
            }}
          >
            {isRotationSnapping && (
              <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="opacity-80">
                <circle cx="2" cy="5" r="1.5" />
                <circle cx="8" cy="5" r="1.5" />
              </svg>
            )}
            <span>{rotationAngle}°</span>
          </div>
        )}

        {showMenu && activeObject && (
          <div className="absolute z-50 tooltip-graphic-info mt-2 bg-white rounded-lg shadow-lg py-1 text-sm text-gray-700">
            {menuOptions.map((option, index) => (
              <button
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  option.onClick();
                  setShowMenu(false);
                }}
                className={`w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2 ${option.className || ''}`}
              >
                {option.icon}
                <span>{option.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Keyboard Shortcuts Button */}
        <Dialog open={showKeyboardShortcuts} onOpenChange={setShowKeyboardShortcuts}>
          <button
            onClick={() => setShowKeyboardShortcuts(true)}
            className="tooltip-graphic-info absolute top-8 right-4 z-[100] bg-white hover:bg-gray-100 text-gray-700 rounded-full p-3 transition-all hover:scale-110 border border-gray-200"
            title="Tangentbordsgenvägar (?)">
            <HelpCircle size={20} />
          </button>

          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HelpCircle size={24} />
                Tangentbordsgenvägar
              </DialogTitle>
              <DialogDescription>
                Arbeta snabbare med dessa genvägar
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-3 py-4">
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm font-medium">Ångra</span>
                <div className="flex gap-1 items-center">
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Ctrl</kbd>
                  <span className="text-xs text-muted-foreground">+</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Z</kbd>
                </div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm font-medium">Gör om</span>
                <div className="flex gap-1 items-center">
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Ctrl</kbd>
                  <span className="text-xs text-muted-foreground">+</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Y</kbd>
                </div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">Ta bort grafik</span>
                <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Delete</kbd>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">Duplicera grafik</span>
                <div className="flex gap-1 items-center">
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Ctrl</kbd>
                  <span className="text-xs text-muted-foreground">+</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">D</kbd>
                </div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">Flytta grafik (1px)</span>
                <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">← ↑ → ↓</kbd>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">Flytta grafik (10px)</span>
                <div className="flex gap-1 items-center">
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Shift</kbd>
                  <span className="text-xs text-muted-foreground">+</span>
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Pilar</kbd>
                </div>
              </div>
              
              <div className="flex justify-between items-center py-2 border-b">
                <span className="text-sm">Rotera med snäpp (45°)</span>
                <div className="flex gap-1 items-center">
                  <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Shift</kbd>
                  <span className="text-xs text-muted-foreground">+</span>
                  <span className="text-xs">Dra rotation</span>
                </div>
              </div>
              
              <div className="flex justify-between items-center py-2">
                <span className="text-sm">Visa denna panel</span>
                <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">?</kbd>
              </div>
            </div>
            
            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground text-center">
                Tryck <kbd className="px-1 py-0.5 bg-muted rounded text-xs font-mono">Esc</kbd> för att stänga
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
});

export default CarPreview;